import {Component, HostListener, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatSidenav} from '@angular/material/sidenav';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer, Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, Router, UrlTree} from '@angular/router';
import {SessionService} from './services/session/session.service';
import {ApiService} from './services/api/api.service';
import {TeamsService} from './services/teams/teams.service';
import {UiAlertService} from './services/ui-alert/ui-alert.service';
import {SpinnerService} from './services/spinner/spinner.service';
import {Plan, StripeService} from './services/stripe/stripe.service';
import {SnackBarComponent} from './components/snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import { UsersService } from './services/users/users.service';
import {TransactionsService} from './services/transactions/transactions.service';
import {AppSettings} from './app.settings';
import {MetaService} from './services/meta/meta.service';
import {Dictionary} from 'highcharts';
import packageInfo from '../../package.json';
import {UtilsService} from './services/utils/utils.service';
import {Roles, TeamCompanyUsersService} from './services/teamcompanyusers/teamcompanyusers.service';
import {DEFAULT_INTERRUPTSOURCES, Idle} from '@ng-idle/core';
import {MatDialogRef} from '@angular/material/dialog';
import {ConfirmDialogComponent} from './components/modal/confirmdialog/confirmdialog.component';
import {ClientService} from './services/client/client.service';
import {SecretService} from "./services/secret/secret.service";
import {configureCognitoAuth} from './services/cognito/amplify-auth-config';
import {SecretsInterface} from "./interfaces/secrets/secrets.interface";
import {BrandsService} from './services/brands/brands.service';
import { PayrollAccessService } from './services/payroll-access/payroll-access.service';
import { PayrollAdminLandingService } from './services/payroll-admin-landing/payroll-admin-landing.service';
import { ManagedFeatureFlagsService } from './services/managed-feature-flags/managed-feature-flags.service';
import { PAYROLL_ADMIN_COMPANY_THEMING_FLAG } from './components/payrolladmin/payroll-admin-feature-flags';
import { ADMIN_SHARED_SHELL_FLAG } from './services/managed-feature-flags/managed-feature-flag-keys';
import { normalizeLogoUrl } from './helpers/logo-url.helper';
import { ActiveEmployerCompanyService } from './services/active-employer-company/active-employer-company.service';
import { NativeTeamContextService } from './services/native-team-context/native-team-context.service';
import { TeamManagerWorkspaceService } from './services/team-manager-workspace/team-manager-workspace.service';
import { CompanyOffersAccessService } from './services/company-offers/company-offers-access.service';
import { VersionGateDialogService } from './services/version-policy/version-gate-dialog.service';
import { VersionPolicyService } from './services/version-policy/version-policy.service';

interface SensitiveDataAccessPill {
  capability: string;
  label: string;
}

const SENSITIVE_DATA_ACCESS_LABELS: Record<string, string> = {
  VIEW_FULL_SSN: 'View Full SSN',
  DELIVER_FULL_SSN: 'Deliver Full SSN',
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {

  @ViewChild('sidenav') sidenav?: MatSidenav;
  title = 'Team Admin';
  public ready = false;
  public toolbarClass = '';
  public wellspaces: Array<any> = [];
  public filteredWellspaces: Array<any> = [];
  public currentWellspace: any = {};
  public isLoggedIn = false;
  public showLoading = false;
  public spinnerMessage = '';
  public wellspaceFilter = '';
  public badgeCount = 0;
  public approvalCount = 0;

  public isIntegrationAdmin = AppSettings.isIntegration();
  public isContentAdmin = AppSettings.isContentAdmin();
  public isCustomerSupport = AppSettings.isCustomerSupport();
  public isBrokerAdmin = AppSettings.isBroker();
  public isPayrollAdmin = AppSettings.isPayrollAdmin();
  public canAccessPayrollAdmin = false;
  public canShowPayrollAdminNav = false;
  public canAccessPayrollExpenseApprovals = false;
  public isTeamAgentSession = false;
  public teamAdminWellspaceCreationEnabled = false;
  public isTeamAgentOnly = false;
  public isViewingPayrollAdmin = false;
  public isNativeCompanyManager = false;
  public isSupportedSingleCompanyWorkspaceActive = false;
  public isNavigationReady = false;
  public activeEmployerCompany: any | undefined;
  public nativeCompanyHasBenefits = false;
  public isPayrollAdminThemeReady = !this.isPayrollAdmin;
  public isFinanceAdmin = AppSettings.isFinance();
  public isSharedAdminShellFlagEnabled = false;
  public showPayrollDevelopmentSettings = AppSettings.getEnv() !== 'prod';
  public isSuperAdmin = false;
  public isExactSuperAdmin = false;
  public isTeamAdminOrUnmatched = !this.isIntegrationAdmin
    && !this.isCustomerSupport
    && !this.isBrokerAdmin
    && !this.isContentAdmin
    && !this.isPayrollAdmin;

  public company: Dictionary<any> | undefined;
  public payrollCompanyName = '';
  public user: Dictionary<any> | undefined;
  public spinnerLevel = 0;
  public releaseVersion = '';
  public info = packageInfo.version;
  public roles = {} as Roles;

  public embedded = false;
  public currentUserInfo = '';
  public currentUserDisplayName = '';
  public currentUserInitials = '?';
  public sensitiveDataAccessPills: SensitiveDataAccessPill[] = [];
  public brandUrl = '';
  public sidenavMode: 'side' | 'over' = window.innerWidth < 601 ? 'over' : 'side';
  public sidenavOpened = window.innerWidth >= 601;
  private inactivityDialog: MatDialogRef<ConfirmDialogComponent, any> | undefined;
  private showAlertWhenCreateWellspace = false;
  private pendingApprovalRequestId = 0;
  private navigationSessionUserId = '';
  private navigationResolutionVersion = 0;
  private payrollAccessRequestId = 0;
  private sharedShellRequestId = 0;
  protected readonly AppSettings = AppSettings;
  protected readonly window = window;
  protected TeamsService = TeamsService;

  public payrollSectionExpanded = window.innerWidth >= 601;

  get canRenderAppShell(): boolean {
    return !this.isLoggedIn
      || !this.isPayrollAdmin
      || this.isPayrollAdminThemeReady
      || this.router.url.includes('/login')
      || this.router.url.includes('/2fa');
  }

  get isPayrollAdminHostMode(): boolean {
    return AppSettings.isPayrollAdmin() && this.canAccessPayrollAdmin;
  }

  get isViewingNativeCompanyWorkspace(): boolean {
    return this.isSupportedSingleCompanyWorkspaceActive
      && this.router.url.split('?')[0].startsWith('/payrolladmin');
  }

  get nativeWorkspaceTeamName(): string {
    if (!this.nativeTeamContextService.state.active) {
      return '';
    }
    const selectedTeamId = `${this.currentWellspace?.id || ''}`.trim();
    if (!selectedTeamId) {
      return '';
    }
    const selectedTeam = this.nativeTeamContextService.state.teams.find(
      (team) => `${team?.id || ''}` === selectedTeamId,
    );
    return `${selectedTeam?.name || ''}`.trim();
  }

  get canShowPayrollAdminNavigation(): boolean {
    return this.isNativeCompanyManager
      ? this.isSupportedSingleCompanyWorkspaceActive
      : this.canShowPayrollAdminNav;
  }

  get canShowNativeBilling(): boolean {
    return !!this.currentWellspace?.name
      && !this.isTeamAgentSession
      && !this.isTeamAgentOnly;
  }

  get canRenderNativeNavigation(): boolean {
    return this.isNavigationReady
      && (this.isSupportedSingleCompanyWorkspaceActive || this.isNativeTeamManager);
  }

  get isNativeTeamManager(): boolean {
    return this.teamManagerWorkspaceService?.useNativeNavigation === true;
  }

  get canRenderEstablishedNavigation(): boolean {
    return this.isNavigationReady
      && !this.isSupportedSingleCompanyWorkspaceActive && !this.isNativeTeamManager;
  }

  get useSharedAdminShell(): boolean {
    return (this.isSharedAdminShellFlagEnabled || this.isNativeTeamManager)
      && !this.isShellBypassRoute();
  }

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    public router: Router,
    private sessionService: SessionService,
    private spinnerService: SpinnerService,
    private titleService: Title,
    private stripeService: StripeService,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private teamsService: TeamsService,
    private uiAlertService: UiAlertService,
    private usersService: UsersService,
    private transactionsService: TransactionsService,
    private metaService: MetaService,
    private utilsService: UtilsService,
    private teamCompanyUsersService: TeamCompanyUsersService,
    private idle: Idle,
    public clientService: ClientService,
    private secretService: SecretService,
    private brandsService: BrandsService,
    private payrollAccessService: PayrollAccessService,
    private payrollAdminLandingService: PayrollAdminLandingService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private nativeTeamContextService: NativeTeamContextService,
    private versionPolicyService: VersionPolicyService,
    private versionGateDialogService: VersionGateDialogService,
    public teamManagerWorkspaceService: TeamManagerWorkspaceService,
    public companyOffersAccess: CompanyOffersAccessService,
  ) {
    this.versionPolicyService.initialize();
    this.versionGateDialogService.initialize();
    this.spinnerService.visibility.subscribe(visible => {
      this.showLoading = visible;
      this.spinnerMessage = this.spinnerService.message;
    });
    if (!(AppSettings.isLocal || AppSettings.isQa)) {
      idle.setIdle(300);
      idle.setTimeout(30);
      idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
      idle.onTimeoutWarning.subscribe(countdown => {
        if (this.shouldApplyInactivityLogout()) {
          const message = `You will be logged out due to inactivity in ${countdown} seconds. Do you wish to stay logged in?`;
          if (!this.inactivityDialog) {
            this.uiAlertService.presentAlertConfirm(message, (ref) => {
              this.inactivityDialog = ref
            }).then(confirm => {
              if (!confirm && this.shouldApplyInactivityLogout()) {
                this.logoutClicked(true);
              }
            });
          } else {
            this.inactivityDialog.componentInstance.message = message;
            this.inactivityDialog.componentInstance.update();
          }
        }
      });
      idle.onTimeout.subscribe(() => {
        if (this.shouldApplyInactivityLogout()) {
          this.logoutClicked(true);
        }
      });
      this.idle.watch();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.configureSideNav();
  }

  private shouldApplyInactivityLogout(): boolean {
    return this.isLoggedIn && !this.sessionService.isSuperAdmin();
  }

  configureSideNav(): void {
    const smallScreen = window.innerWidth < 601;
    if (!smallScreen) {
      this.sidenavMode = 'side';
      this.sidenavOpened = true;
    } else {
      this.sidenavMode = 'over';
      this.sidenavOpened = false;
    }
  }
  ngOnInit(): void {
    this.configureAws();
    if (this.isCustomerSupport) {
      this.titleService.setTitle('Customer Support');
    }
    this.releaseVersion = AppSettings.getReleaseVersion();
    this.apiService.spinnerSubscribe((level => {
      this.spinnerLevel = level;
    }));
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
      if (wellspace === undefined) {
        return;
      } else if (wellspace) {
        this.setWellspace(wellspace);
        void this.stripeService.checkIfHasSomePendingAch(wellspace.id);
      } else {
        this.clearWellspacePresentation();
      }
    });
    this.apiService.monitorLoginState(loggedIn => {
      const wasLoggedIn = this.isLoggedIn;
      this.isLoggedIn = loggedIn;
      if (!loggedIn) {
        this.resetNavigationResolution();
        this.isExactSuperAdmin = false;
        if (wasLoggedIn) {
          this.teamsService.clearSelectedWellspace();
        }
        this.nativeTeamContextService.clear();
        this.ready = false;
        this.isLoggedIn = false;
        this.isSharedAdminShellFlagEnabled = false;
        this.isNativeCompanyManager = false;
        this.activeEmployerCompanyService.clear();
        this.badgeCount = 0;
        this.approvalCount = 0;
        this.company = undefined;
        this.user = undefined;
        this.currentUserInfo = '';
        this.currentUserDisplayName = '';
        this.currentUserInitials = '?';
        this.sensitiveDataAccessPills = [];
        this.teamAdminWellspaceCreationEnabled = false;
      } else {
        const userId = this.sessionService.getUserId();
        if (!wasLoggedIn || this.navigationSessionUserId !== userId) {
          this.resetNavigationResolution(userId);
        }
        void this.refreshTeamAdminWellspaceCreationCapability(userId);
        if (AppSettings.isIntegration()) {
          this.metaService.init();
          setTimeout(() => {
            this.sessionService.getCompany().then(company => {
              setTimeout(() => {});
              this.company = company;
            });
            this.sessionService.getUser().then(user => {
              this.user = user;
            });
          }, 100);  // wait for JWT session token to be set
        }
      }
    });
    this.stripeService.pendingACHCount.subscribe(count => {
      this.badgeCount = count;
    });
    const apiParam = new URLSearchParams(document.location.search).get('api') || '';
    this.apiService.setBaseUrl(AppSettings.getEndpoint(), apiParam);
    [
      { name: 'plans', path: 'assets/icons/rocket-launch-outline.svg'},
      { name: 'bell', path: 'assets/icons/bell.svg'},
      { name: 'wallit', path: 'assets/logo/WallitLogo.svg'},
      { name: 'icon-eye-lock', path: 'assets/icons/icon-eye-lock.svg'},
      { name: 'icon-eye', path: 'assets/icons/icon-eye.svg'},
    ].forEach(icon => {
      this.matIconRegistry.addSvgIcon(
        icon.name,
        this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path)
      );
    });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.embedded = event.urlAfterRedirects.includes('insuranceprofile/embedded') || event.urlAfterRedirects.includes('brokeragency/member/enroll');
        this.toolbarClass = event.urlAfterRedirects === '/login' ? 'hidetoolbar' : '';
        if (this.isNavigationReady) {
          const navigationVersion = this.navigationResolutionVersion;
          void this.refreshPayrollAccess(false, navigationVersion);
          void this.refreshSharedAdminShellFlag(navigationVersion);
        }
        if (event.urlAfterRedirects === '/' && this.sessionService.createTeam) {
          this.sessionService.createTeam = false;
          this.addWellspace();
        }
      }
    });
    this.usersService.meSubscribe(async (response: any) => {
      if (response) {
        const userId = this.sessionService.getUserId();
        const responseUserId = `${response?.user?.id || ''}`.trim();
        if (
          !this.apiService.isAuthenticated()
          || !userId
          || (responseUserId && responseUserId !== userId)
        ) {
          return;
        }
        if (this.navigationSessionUserId !== userId) {
          this.resetNavigationResolution(userId);
        }
        const navigationVersion = this.navigationResolutionVersion;
        const payrollAccessWasApplied = await this.refreshPayrollAccess(
          !this.isNavigationReady,
          navigationVersion,
        );
        if (
          !payrollAccessWasApplied
          || !this.isCurrentNavigationResolution(userId, navigationVersion)
        ) {
          return;
        }
        this.currentUserInfo = this.utilsService.getUserName(response.user);
        this.currentUserDisplayName = this.getCurrentUserDisplayName(response.user);
        this.currentUserInitials = this.getCurrentUserInitials(response.user);
        this.sensitiveDataAccessPills = this.getSensitiveDataAccessPills(
          response.sensitiveDataAccessCapabilities,
        );
        this.applyPayrollUserBrandFallback(response);
        this.brandUrl = this.usersService.me().brand?.horizontalLogoOnWhite;
        if (this.isViewingPayrollAdmin) {
          this.brandUrl = this.resolvePayrollBrandUrl(this.usersService.getCachedTeamCompanies() || []);
        }
        this.isSuperAdmin = this.usersService.isAdmin();
        this.isExactSuperAdmin = this.sessionService.isExactSuperAdmin();
        const [sharedShellWasApplied, managersResult] = await Promise.all([
          this.refreshSharedAdminShellFlag(navigationVersion),
          this.teamCompanyUsersService.getManagers()
            .then(result => ({result, error: undefined}))
            .catch(error => ({result: undefined, error})),
        ]);
        if (
          !sharedShellWasApplied
          || !this.isCurrentNavigationResolution(userId, navigationVersion)
        ) {
          return;
        }
        if (managersResult.error) {
          console.error(managersResult.error);
          this.isTeamAgentOnly = false;
        } else {
          this.isTeamAgentOnly = this.hasOnlyTeamAgentRoles(managersResult.result);
        }
        this.roles = this.teamCompanyUsersService.roles || ({} as Roles);
        this.isTeamAgentSession = this.sessionService.isTeamAgentSession();
        const teamWorkspace = await this.teamManagerWorkspaceService.refresh();
        if (!this.isCurrentNavigationResolution(userId, navigationVersion)) return;
        if (this.teamManagerWorkspaceService.useNativeNavigation) {
          this.wellspaces = this.filteredWellspaces = [...teamWorkspace.teams];
          await this.teamManagerWorkspaceService.ensureSelectedTeam();
          if (!this.isCurrentNavigationResolution(userId, navigationVersion)) return;
        }
        this.ready = true;
        this.completeNavigationResolution(userId, navigationVersion);
        if (this.nativeTeamContextService.state.active) {
          this.wellspaces = this.filteredWellspaces = [
            ...this.nativeTeamContextService.state.teams,
          ];
        } else {
          void this.initializeTeamContextForCurrentUser(userId, navigationVersion);
        }
        if (this.isCustomerSupport || this.isBrokerAdmin) {
          if (!this.roles.supportAgent && !this.roles.manager && !this.roles.supportAdmin && !this.isSuperAdmin && !this.roles.broker && !this.roles.agency && !this.roles.member && !this.roles.client && !this.roles.teamAgent) {
            this.uiAlertService.presentMessage('You are neither an admin, broker, agent, member, client, team agent, customer support manager, or customer support agent. You will be logged out.').then(() => {
              this.logoutClicked(false);
            });
          }
        }
      }
    });
  }

  private async refreshTeamAdminWellspaceCreationCapability(userId: string): Promise<void> {
    if (this.sessionService.isTeamAgentSession()) {
      this.teamAdminWellspaceCreationEnabled = true;
      return;
    }

    try {
      const enabled = await this.teamsService.isTeamAdminWellspaceCreationEnabled();
      if (this.isLoggedIn && this.sessionService.getUserId() === userId) {
        this.teamAdminWellspaceCreationEnabled = enabled;
      }
    } catch (error) {
      if (this.sessionService.getUserId() === userId) {
        this.teamAdminWellspaceCreationEnabled = false;
      }
      console.error('Unable to resolve Team Admin Wellspace creation access', error);
    }
  }

  private configureAws(): void {
    this.secretService.secrets.subscribe({
      next: (secrets: SecretsInterface | null): void => {
        if (secrets) {
          configureCognitoAuth(secrets, window.sessionStorage);
        }
      }
    });
  }

  private hasOnlyTeamAgentRoles(result: any): boolean {
    const roles = result?.plain?.() || result || [];
    return Array.isArray(roles)
      && roles.length > 0
      && roles.every((role: any) => role?.role === 'teamagent');
  }

  setWellspace(wellspace: any): void {
    this.currentWellspace = wellspace;
    wellspace.role = wellspace.ownerUserId === this.sessionService.getUserId() ? '' : 'admin';
    if (wellspace && this.usersService.getCurrentUserId()) {
      const requestId = ++this.pendingApprovalRequestId;
      this.transactionsService.getPendingReceiptApprovalCount(wellspace.id).then(count => {
        if (requestId === this.pendingApprovalRequestId && this.currentWellspace?.id === wellspace.id) {
          this.approvalCount = count;
        }
      });
    }
  }

  private clearWellspacePresentation(): void {
    this.pendingApprovalRequestId++;
    this.currentWellspace = {};
    this.badgeCount = 0;
    this.approvalCount = 0;
    if (this.isLoggedIn && this.isWellspaceRequiredRoute()) {
      void this.router.navigate(['/']);
    }
  }

  private isWellspaceRequiredRoute(): boolean {
    // A destination guard may clear the old context before router.url changes.
    // Do not cancel navigation away from Billing/Analytics with a Home redirect.
    const pendingUrl = this.router.getCurrentNavigation()?.finalUrl;
    const path = (pendingUrl ? this.router.serializeUrl(pendingUrl) : this.router.url).split('?')[0];
    return path === '/analytics'
      || path.startsWith('/analytics/')
      || path === '/billing'
      || path.startsWith('/billing/');
  }

  leftTitle(): string {
    if (!this.isBrokerAdmin && !this.isCustomerSupport && !this.isContentAdmin && !this.isPayrollAdminHostMode) {
      return `Wallit${this.releaseVersion ? ' ' + this.releaseVersion : ''} ${this.isIntegrationAdmin ? 'Integration ' : ''}Admin`;
    }
    if (this.isPayrollAdminHostMode) {
      return 'Wallit Payroll Admin';
    }
    if (this.isBrokerAdmin) {
      return 'Wallit Broker & Agency Admin';
    }
    if (this.isCustomerSupport) {
      return `Customer Support ${this.isSuperAdmin ? 'Super Admin' : this.roles?.supportAdmin ? 'Admin' : 'Agent'}`;
    }
    if (this.isContentAdmin) {
      return `Content ${this.isSuperAdmin ? 'Super Admin' : this.roles?.supportAdmin ? 'Admin' : 'Agent'}`;
    }
    return '';
  }

  private loadWellspaces(
    name?: string,
    userId = this.sessionService.getUserId(),
    navigationVersion = this.navigationResolutionVersion,
  ): void {
    if (this.isLoggedIn && !this.isIntegrationAdmin && !this.isBrokerAdmin && !this.isContentAdmin && !this.isPayrollAdmin && !this.isPayrollAdminHostMode) {
      setTimeout(() => {
        if (!this.isCurrentNavigationResolution(userId, navigationVersion)) {
          return;
        }
        this.teamsService.getUserTeamsInfo(this.sessionService.getUserId()).then(teams => {
          if (!this.isCurrentNavigationResolution(userId, navigationVersion)) {
            return;
          }
          this.wellspaces = this.filteredWellspaces = teams;
          this.wellspaces.forEach(wellspace => {
            wellspace.role = wellspace.ownerUserId === this.sessionService.getUserId() ? '' : 'admin';
          });
          this.ready = true;
          const currentWellspaceId = this.teamsService.wellspaceChangedObservable.value?.id;
          if (teams.length === 1 && currentWellspaceId !== teams[0].id) {
            this.gotoWellspace(teams[0]);
          } else if (name) {
            const namedWellspace = teams.find((team: any) => team.name === name);
            if (namedWellspace && currentWellspaceId !== namedWellspace.id) {
              this.gotoWellspace(namedWellspace);
            }
          }
        }).catch(() => {
          if (!this.isCurrentNavigationResolution(userId, navigationVersion)) {
            return;
          }
          this.wellspaces = this.filteredWellspaces = [];
          this.ready = true;
        });
      }, 100);
    } else {
      this.wellspaces = this.filteredWellspaces = [];
      if (this.isCurrentNavigationResolution(userId, navigationVersion)) {
        this.ready = true;
      }
    }
  }

  private async initializeTeamContextForCurrentUser(
    userId: string,
    navigationVersion: number,
  ): Promise<void> {
    if (
      !this.isCurrentNavigationResolution(userId, navigationVersion)
    ) {
      return;
    }

    if (this.nativeTeamContextService.state.active) {
      this.wellspaces = this.filteredWellspaces = [
        ...this.nativeTeamContextService.state.teams,
      ];
      this.ready = true;
      return;
    }

    await this.teamsService.ensureValidatedSelectedWellspace();
    if (
      this.isCurrentNavigationResolution(userId, navigationVersion)
    ) {
      this.loadWellspaces(undefined, userId, navigationVersion);
    }
  }

  applyFilter(event: any): void {
    event.stopPropagation();
    let value = event.target.value;
    value = value.trim();
    value = value.toLowerCase();
    this.wellspaceFilter = value;
    this.setFilter();
    setTimeout(() => {
      event.target.focus();
    }, 200);
  }

  clearFilter(event: any): void {
    event.stopPropagation();
    this.wellspaceFilter = '';
    this.setFilter();
  }

  private setFilter(): void {
    this.filteredWellspaces = this.wellspaces.filter(wellspace => wellspace.name.toLowerCase().includes(this.wellspaceFilter));
  }

  toggleNav(): void {
    if (this.sidenav) {
      void this.sidenav.toggle();
    } else {
      this.sidenavOpened = !this.sidenavOpened;
    }
  }

  isRouteActive(route: string): boolean {
    const currentPath = this.normalizeRoutePath(this.router.url.split('?')[0]);
    const targetPath = this.normalizeRoutePath(route);

    if (!targetPath) {
      return currentPath === '';
    }

    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
  }

  wallitClicked(): void {
    this.gotoApp();
  }

  gotoApp(): void {
    this.usersService.loginUserV3(this.usersService.getCurrentUserId(), false, null);
  }

  navigate(route: string, preserve: boolean = false): void {
    // this.isBrokerAdmin = route === 'externalbrokeradmin';
    if (preserve) {
      this.router.navigate([route], { queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate([route], {queryParams: AppSettings.getLocalNavigationQueryParams()});
    }
  }

  async navigateToPayrollAdmin(): Promise<void> {
    if (this.isNativeCompanyManager) {
      void this.router.navigateByUrl(
        this.isSupportedSingleCompanyWorkspaceActive ? '/payrolladmin/teams' : '/',
      );
      return;
    }
    const nextRoute = await this.payrollAdminLandingService.getLandingRoute();
    void this.router.navigate([nextRoute], {queryParams: AppSettings.getLocalNavigationQueryParams()});
  }

  logoutClicked(showTimeoutPopup: boolean = false): void {
    this.sidenav?.close();
    this.sessionService.logout();
    this.router.navigate(['login', showTimeoutPopup]).then(() => window.location.reload());
  }

  notificationsClicked(): void {
    this.snackBar.openFromComponent(SnackBarComponent, {data: 'Coming soon!'});
  }

  launchClicked(): void {
    this.gotoApp();
  }

  helpClicked(): void {
    window.open('https://help.wallit.app');
  }

  plansClicked(): void {
    window.open('https://help.wallit.app/pages/Wallit-plans-and-features.html');
  }

  actionsClicked(): void {
    this.router.navigate(['admin/actions']);
  }

  supportUsersClicked(): void {

  }

  addWellspace(): void {
    const checkAllTeamUserToSeeIfAdmin: boolean = this.wellspaces.some(team => team && team.role && team.role === 'admin');
    if ( checkAllTeamUserToSeeIfAdmin ) {
      const msg = 'Are you sure you want to proceed? You will become the team owner, ' +
          'responsible for setting up payment methods. You might prefer to ask your team admin to create a new team with you as the delegated admin.';
      this.uiAlertService.presentAlertConfirm(msg).then((confirm: boolean): void => {
        if (confirm) {
          this.createNewWellspace();
        }
      });
    } else {
      this.createNewWellspace();
    }
  }

  private async refreshPayrollAccess(
    forceRefresh = false,
    navigationVersion = this.navigationResolutionVersion,
  ): Promise<boolean> {
    const userId = this.sessionService.getUserId();
    const requestId = ++this.payrollAccessRequestId;
    if (!this.apiService.isAuthenticated() || !this.sessionService.getUserId()) {
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
        return false;
      }
      this.canAccessPayrollAdmin = false;
      this.canShowPayrollAdminNav = false;
      this.canAccessPayrollExpenseApprovals = false;
      this.isViewingPayrollAdmin = false;
      this.isNativeCompanyManager = false;
      this.nativeTeamContextService.clear();
      this.activeEmployerCompanyService.clear();
      this.isSupportedSingleCompanyWorkspaceActive = false;
      this.activeEmployerCompany = undefined;
      this.nativeCompanyHasBenefits = false;
      this.payrollCompanyName = '';
      this.brandsService.clearCompanyThemeOverride();
      this.isPayrollAdminThemeReady = true;
      return true;
    }

    try {
      const access = await this.payrollAccessService.refresh(forceRefresh);
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
        return false;
      }
      this.canAccessPayrollAdmin = access.canAccessPayrollAdmin;
      this.canShowPayrollAdminNav = access.canShowPayrollAdminNav;
      this.canAccessPayrollExpenseApprovals = access.canAccessPayrollExpenseApprovals;
      this.isViewingPayrollAdmin = access.isViewingPayrollAdmin;
      this.isNativeCompanyManager = access.workspaceContext === 'nativeCompanyManager';
      const isScopedSuperadminCompanyWorkspace =
        this.isScopedSuperadminCompanyWorkspaceRoute(access.canShowPayrollAdminNav);

      if (this.isNativeCompanyManager) {
        const activeCompanyState = await this.activeEmployerCompanyService.initialize(forceRefresh);
        if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
          return false;
        }
        const nativeTeamState = await this.nativeTeamContextService.initialize(false);
        if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
          return false;
        }
        this.isSupportedSingleCompanyWorkspaceActive =
          this.activeEmployerCompanyService.isSupportedSingleCompanyWorkspaceActive(activeCompanyState);
        this.activeEmployerCompany = activeCompanyState.activeCompany;
        this.nativeCompanyHasBenefits = activeCompanyState.hasBenefits;
        if (nativeTeamState.active) {
          this.wellspaces = this.filteredWellspaces = [...nativeTeamState.teams];
        }
      } else if (isScopedSuperadminCompanyWorkspace) {
        const activeCompanyState = this.activeEmployerCompanyService.state;
        this.isSupportedSingleCompanyWorkspaceActive = false;
        this.activeEmployerCompany = activeCompanyState.activeCompany;
        this.nativeCompanyHasBenefits = activeCompanyState.hasBenefits;
      } else {
        // During guard execution router.url still names the previous page. Keep
        // the pending company load; the guard will authorize it before activation.
        const pendingUrl = this.router.getCurrentNavigation()?.finalUrl;
        const hasPendingScopedCompanyNavigation = access.canShowPayrollAdminNav
          && this.sessionService.isExactSuperAdmin()
          && !!this.getScopedSuperadminCompanyRouteId(pendingUrl);
        if (!hasPendingScopedCompanyNavigation) {
          this.nativeTeamContextService.clear();
          this.activeEmployerCompanyService.clear();
        }
        this.isSupportedSingleCompanyWorkspaceActive = false;
        this.activeEmployerCompany = undefined;
        this.nativeCompanyHasBenefits = false;
      }

      let payrollCompanies: any[] = [];
      if (this.isPayrollAdminHostMode) {
        payrollCompanies = this.usersService.getCachedTeamCompanies() || await this.usersService.getTeamCompanies();
        if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
          return false;
        }
        this.payrollCompanyName = payrollCompanies?.[0]?.name || '';
      } else {
        this.payrollCompanyName = '';
      }
      await this.refreshPayrollCompanyTheme(payrollCompanies);
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
        return false;
      }

      const me = this.usersService.me();
      if (me) {
        this.applyPayrollUserBrandFallback(me);
        this.brandUrl = this.isPayrollAdminHostMode
          ? this.resolvePayrollBrandUrl(payrollCompanies)
          : me?.brand?.horizontalLogoOnWhite;
      }
      this.isPayrollAdminThemeReady = true;
      return true;
    } catch (error) {
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.payrollAccessRequestId)) {
        return false;
      }
      this.canAccessPayrollAdmin = false;
      this.canShowPayrollAdminNav = false;
      this.canAccessPayrollExpenseApprovals = false;
      this.isViewingPayrollAdmin = false;
      this.isNativeCompanyManager = false;
      this.nativeTeamContextService.clear();
      this.activeEmployerCompanyService.clear();
      this.isSupportedSingleCompanyWorkspaceActive = false;
      this.activeEmployerCompany = undefined;
      this.nativeCompanyHasBenefits = false;
      this.payrollCompanyName = '';
      this.brandsService.clearCompanyThemeOverride();
      this.isPayrollAdminThemeReady = true;
      return true;
    }
  }

  private isScopedSuperadminCompanyWorkspaceRoute(nativeAccessFlagEnabled: boolean): boolean {
    if (!nativeAccessFlagEnabled || !this.sessionService.isExactSuperAdmin()) {
      return false;
    }

    const parsedUrl = this.router.parseUrl(this.router.url || '');
    const requestedCompanyId = this.getScopedSuperadminCompanyRouteId(parsedUrl);
    const activeCompanyId = `${this.activeEmployerCompanyService.state.activeCompany?.id || ''}`.trim();

    // Billing uses the validated selection rather than company query parameters.
    // Keep that context after NavigationEnd; clearing it redirects Billing home.
    if (parsedUrl.root.children.primary?.segments[0]?.path === 'billing') {
      const teamState = this.nativeTeamContextService.state;
      const teamId = this.teamsService.wellspaceChangedObservable.value?.id;
      return this.activeEmployerCompanyService.isScopedSuperadminCompanyWorkspaceActive()
        && teamState.active && teamState.companyId === activeCompanyId
        && !!teamId && teamState.teams.some(team => team.id === teamId);
    }

    return !!requestedCompanyId
      && requestedCompanyId === activeCompanyId
      && this.activeEmployerCompanyService.isScopedSuperadminCompanyWorkspaceActive();
  }

  private getScopedSuperadminCompanyRouteId(parsedUrl?: UrlTree): string {
    if (!parsedUrl) {
      return '';
    }
    const segments = parsedUrl.root.children.primary?.segments || [];
    const isScopedTeamsRoute = ((segments[0]?.path === 'payrolladmin'
      && segments[1]?.path === 'teams') || segments[0]?.path === 'offers')
      && parsedUrl.queryParams?.workspace === 'company';
    const requestedCompanyId = `${parsedUrl.queryParams?.companyId || ''}`.trim();
    return isScopedTeamsRoute ? requestedCompanyId : '';
  }

  private async refreshSharedAdminShellFlag(
    navigationVersion = this.navigationResolutionVersion,
  ): Promise<boolean> {
    const userId = this.sessionService.getUserId();
    const requestId = ++this.sharedShellRequestId;
    if (!this.shouldEvaluateSharedAdminShellFlag()) {
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.sharedShellRequestId)) {
        return false;
      }
      this.isSharedAdminShellFlagEnabled = false;
      return true;
    }

    try {
      const me = this.usersService.me();
      const partnerId = `${me?.user?.partnerId || me?.brand?.partnerId || ''}`.trim() || undefined;
      const isEnabled = await this.managedFeatureFlagsService.isEnabled(
        ADMIN_SHARED_SHELL_FLAG,
        'team-admin',
        {
          userId: this.sessionService.getUserId(),
          partnerId,
        },
      );
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.sharedShellRequestId)) {
        return false;
      }
      this.isSharedAdminShellFlagEnabled = isEnabled;
      return true;
    } catch (error) {
      if (!this.isCurrentNavigationRequest(userId, navigationVersion, requestId, this.sharedShellRequestId)) {
        return false;
      }
      this.isSharedAdminShellFlagEnabled = false;
      console.error('Failed to evaluate shared admin shell flag', error);
      return true;
    }
  }

  private resetNavigationResolution(userId = ''): void {
    this.teamManagerWorkspaceService?.clear();
    this.navigationResolutionVersion++;
    this.payrollAccessRequestId++;
    this.sharedShellRequestId++;
    this.navigationSessionUserId = userId;
    this.isNavigationReady = false;
    this.ready = false;
  }

  private completeNavigationResolution(userId: string, navigationVersion: number): boolean {
    if (!this.isCurrentNavigationResolution(userId, navigationVersion)) {
      return false;
    }
    this.isNavigationReady = true;
    return true;
  }

  private isCurrentNavigationResolution(userId: string, navigationVersion: number): boolean {
    return this.isLoggedIn
      && this.apiService.isAuthenticated()
      && !!userId
      && userId === this.navigationSessionUserId
      && userId === this.sessionService.getUserId()
      && navigationVersion === this.navigationResolutionVersion;
  }

  private isCurrentNavigationRequest(
    userId: string,
    navigationVersion: number,
    requestId: number,
    currentRequestId: number,
  ): boolean {
    return requestId === currentRequestId
      && this.isCurrentNavigationResolution(userId, navigationVersion);
  }

  private shouldEvaluateSharedAdminShellFlag(): boolean {
    return this.isLoggedIn
      && !this.isShellBypassRoute();
  }

  private isShellBypassRoute(): boolean {
    const route = this.normalizeRoutePath(this.router.url.split('?')[0]);
    return this.embedded
      || !this.canRenderAppShell
      || route === 'login'
      || route.startsWith('login/')
      || route === 'signup'
      || route.startsWith('signup/')
      || route === '2fa'
      || route.startsWith('2fa/');
  }

  private normalizeRoutePath(route: string): string {
    return `${route || ''}`.replace(/^\/+|\/+$/g, '');
  }

  private async refreshPayrollCompanyTheme(companies: any[] = []): Promise<void> {
    if (!this.isPayrollAdminHostMode) {
      this.brandsService.clearCompanyThemeOverride();
      return;
    }

    try {
      const isEnabled = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_COMPANY_THEMING_FLAG,
        'team-admin',
        {
          userId: this.usersService.getCurrentUserId() || this.sessionService.getUserId() || undefined,
        },
      );

      if (!isEnabled) {
        this.brandsService.clearCompanyThemeOverride();
        return;
      }

      const theme = this.getCompanyTheme(companies?.[0]);
      if (theme) {
        this.brandsService.setCompanyThemeOverride(theme);
      } else {
        this.brandsService.clearCompanyThemeOverride();
      }
    } catch (error) {
      console.error('Failed to evaluate payroll company theming flag', error);
      this.brandsService.clearCompanyThemeOverride();
    }
  }

  private getCompanyTheme(company: any): any | null {
    const payrollConfig = company?.payrollConfig;
    const parsedPayrollConfig = typeof payrollConfig === 'string'
      ? (() => {
        try {
          return JSON.parse(payrollConfig);
        } catch (_) {
          return {};
        }
      })()
      : (payrollConfig || {});
    const theme = parsedPayrollConfig?.payrollCompany?.theme;
    return theme && typeof theme === 'object' ? theme : null;
  }

  private resolvePayrollBrandUrl(companies: any[] = []): string {
    return this.getCompanyLogoUrl(companies?.[0]) || this.getBrandLogoFallbackUrl();
  }

  private applyPayrollUserBrandFallback(me: any): void {
    if (this.isPayrollAdminHostMode) {
      this.brandsService.setPayrollAdminUserBrandFallback(me?.brand);
    }
  }

  private getCompanyLogoUrl(company: any): string {
    return normalizeLogoUrl(company?.logoUrl);
  }

  private getBrandLogoFallbackUrl(): string {
    return normalizeLogoUrl(this.brandsService.getBrand()?.logoUrl);
  }

  onBrandLogoError(): void {
    const fallbackUrl = this.getBrandLogoFallbackUrl();
    this.brandUrl = fallbackUrl && this.brandUrl !== fallbackUrl ? fallbackUrl : '';
  }

  createNewWellspace(): void {
    this.utilsService.addWellspace().then((result: string | Dictionary<any>): void => {
      if (result.length === 0) { return; }
      if (typeof result === 'string') {
        this.loadWellspaces(result);
      } else {
        void this.teamsService.setWellspace(result);
      }
    });
  }

  gotoWellspace(wellspace: any): void {
    void this.teamsService.setWellspace(wellspace);
  }

  checkClientFeaturePermission(): boolean {
    return this.clientService.checkUserFeaturePermission();
  }

  private getCurrentUserDisplayName(user: any): string {
    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return displayName || user?.nickname || user?.email || '';
  }

  private getSensitiveDataAccessPills(capabilities: unknown): SensitiveDataAccessPill[] {
    if (!Array.isArray(capabilities)) {
      return [];
    }

    return Object.entries(SENSITIVE_DATA_ACCESS_LABELS)
      .filter(([capability]) => capabilities.includes(capability))
      .map(([capability, label]) => ({capability, label}));
  }

  private getCurrentUserInitials(user: any): string {
    const nameParts = [user?.firstName, user?.lastName].filter(Boolean);
    if (nameParts.length > 0) {
      return nameParts
        .slice(0, 2)
        .map((part: string) => part.charAt(0).toUpperCase())
        .join('');
    }

    const fallbackValue = user?.nickname || user?.email || '';
    if (!fallbackValue) {
      return '?';
    }

    const fallbackParts = fallbackValue
      .split(/[\s._@-]+/)
      .filter(Boolean);

    if (fallbackParts.length > 1) {
      return fallbackParts
        .slice(0, 2)
        .map((part: string) => part.charAt(0).toUpperCase())
        .join('');
    }

    return fallbackValue.slice(0, 2).toUpperCase();
  }

}
